/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';

import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Divider,
  Backdrop,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ExportPDFButton from 'src/common/components/ExportPDFButton';
import { useNotes } from 'src/florence/hooks/useNotes';
import { EditNoteParams } from 'src/florence/types/RouterState';
import { initNumberOfResultsFilter } from 'src/florence/utils/initNumberOfResults';

import { usePrefetch } from '../../../common/apis/FlorenceApi';
import { ReactComponent as CheckmarkIcon } from '../../../common/assets/icons/checkmark_icon_blue.svg';
import { ReactComponent as ChevronLeftBlue } from '../../../common/assets/icons/chevron_left_blue.svg';
import { SaveSheet, SheetStatus } from '../../../common/types/SaveSheet';
import ConfirmFinalizePopup from '../../components/ConfirmFinalizePopup/ConfirmFinalizePopup';
import FiltersContainer from '../../components/FiltersContainer/FiltersContainer';
import SearchResultsContainer from '../../components/SearchResultsContainer/SearchResultsContainer';
import { useCodes } from '../../hooks/useCodes';

import CCIAlertBottomBar from './CCIAlertBottomBar';

const EditSheetScreen: React.FC = () => {
  const { note } = useLocation().state as EditNoteParams;

  // filters
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [limit, setLimit] = React.useState<10 | 15 | 25 | 10000>(
    initNumberOfResultsFilter(note?.sheetCodes?.length ?? 0)
  );

  // ui
  const [isSaving, setIsSaving] = React.useState(false);
  const [finalizeOpen, setFinalizeOpen] = React.useState(false);

  // misc
  const navigate = useNavigate();
  const getModifiers = usePrefetch('getModifiers');
  if (!note) {
    navigate(-1);
  }

  // note and codes
  const {
    currentNote,
    finalizeNote,
    updateNote,
    changeClinicNoteStatus,
    updateNoteText,
  } = useNotes([note]);

  const {
    codes,
    selectedCodes,
    refetchCodes,
    isLoading,
    filtersLength,
    selectModifiers,
    selectMultiplier,
    applyFilters,
    clearFilters,
    clinicResponse,
    selectCode,
    selectAllCodes,
    clearSelectedCodes,
    addManualCode,
    incorrectCodeCombinations,
    flagCode,
    updateICD10,
    suggestedICD10,
    AIOutput,
  } = useCodes(
    currentNote,
    currentNote?.scanText ?? '',
    'scan',
    limit,
    updateNote
  );

  // Run on page load
  React.useEffect(() => {
    getModifiers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedAll = (select: boolean) => {
    if (select) selectAllCodes();
    else clearSelectedCodes();
  };

  const onSaveNote = async (showConfirmationMessage = false) => {
    const sheetToSave = {
      ...currentNote,
      sheetCodes: selectedCodes,
      clinicNoteResults: clinicResponse,
      aiOutput: AIOutput,
    };
    if (!sheetToSave) return;

    setIsSaving(true);
    await updateNote(sheetToSave as SaveSheet, showConfirmationMessage);
    setIsSaving(false);
  };

  const updateClinicNoteStatus = async (status: string) => {
    if (!currentNote) return;
    setIsSaving(true);

    clearSelectedCodes();
    await changeClinicNoteStatus(currentNote, status);
    setIsSaving(false);

    refetchCodes();
  };

  const onFinalizeSheet = async () => {
    if (currentNote?.clinicNote) return;

    setIsSaving(true);
    setFinalizeOpen(false);

    const opNote = {
      ...currentNote,
      sheetCodes: selectedCodes,
      aiOutput: AIOutput,
    };
    if (!opNote) return;

    // Save the note first in case the user selected more codes before saving

    setIsSaving(true);
    setFinalizeOpen(false);
    await updateNote(opNote as SaveSheet, false);
    await finalizeNote(opNote as SaveSheet);
    setIsSaving(false);
  };

  return (
    <Box
      className='responsive-padding light-blue-background'
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: '100%',
      }}
    >
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1300,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        open={filtersOpen || finalizeOpen}
      >
        {filtersOpen && (
          <FiltersContainer
            onClickApply={applyFilters}
            onClickClose={() => setFiltersOpen(false)}
            onClickCancelAll={clearFilters}
          />
        )}
        {finalizeOpen && (
          <ConfirmFinalizePopup
            onFinalize={onFinalizeSheet}
            onClickClose={() => {
              setFinalizeOpen(false);
            }}
          />
        )}
      </Backdrop>
      <Container
        maxWidth={false}
        sx={{
          textAlign: 'center',
        }}
      >
        <Stack spacing={'30px'}>
          {window.location.host.includes('admin') && (
            <Box sx={{ display: 'flex' }}>
              <Button variant='text' onClick={() => navigate(-1)}>
                <ChevronLeftBlue />
                <Typography>Back</Typography>
              </Button>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              textAlign: 'left',
              flexWrap: 'wrap',
            }}
          >
            <Box
              style={{
                marginBottom: 12,
                marginRight: 12,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                color='darkBlue'
                fontSize='18px'
                fontWeight={700}
                textAlign='left'
              >
                {currentNote?.clinicNote ? 'Edit Clinic Note' : 'Edit Op Note'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              {(currentNote?.clinicNote ||
                currentNote?.status === SheetStatus.FINALIZED) && (
                <ExportPDFButton isDisabled={isLoading} note={currentNote} />
              )}
              {currentNote?.status !== SheetStatus.FINALIZED &&
                !currentNote?.clinicNote && (
                  <Button
                    disabled={
                      isLoading || isSaving || selectedCodes.length === 0
                    }
                    sx={{
                      borderRadius: '48px',
                      height: '48px',
                      px: '24px',
                    }}
                    onClick={() => setFinalizeOpen(true)}
                    variant='outlined'
                    startIcon={<CheckmarkIcon />}
                    color={currentNote?.clinicNote ? 'primary' : 'darkBlue'}
                  >
                    <Typography color='darkBlue'>Finalize</Typography>
                  </Button>
                )}
              <Button
                onClick={() => onSaveNote(true)}
                disabled={isLoading || isSaving}
                disableElevation
                color={currentNote?.clinicNote ? 'primary' : 'darkBlue'}
                variant='contained'
                sx={{
                  borderRadius: '48px',
                  height: '48px',
                  px: '48px',
                }}
              >
                <Typography color='white'>Save Changes</Typography>
              </Button>
            </Box>
          </Box>
          <Divider />

          {currentNote && (
            <SearchResultsContainer
              hasSelectedAll={
                codes.length !== 0 &&
                codes.filter((c) => !c.selected).length === 0
              }
              selectedAll={selectedAll}
              selectCode={selectCode}
              selectMultiplier={selectMultiplier}
              selectModifiers={selectModifiers}
              limit={limit}
              changeLimit={setLimit}
              searchResults={codes}
              currentNote={currentNote}
              clinicResponse={clinicResponse}
              searchType={'scan'}
              searchText={currentNote?.scanText ?? ''}
              refreshTextSearch={(text) => {
                if (currentNote) {
                  updateNoteText(currentNote, text);
                }
              }}
              refetchCodes={refetchCodes}
              selectedFilterIcon={() => setFiltersOpen(!filtersOpen)}
              filtersLength={filtersLength}
              isLoading={isLoading || isSaving}
              updateClinicNoteStatus={updateClinicNoteStatus}
              clearSelectedCodes={clearSelectedCodes}
              flagCode={flagCode}
              updateNote={updateNote}
              allowNoteTextEditing={true}
              addManualCode={addManualCode}
              updateICD10={updateICD10}
              suggestedICD10={suggestedICD10}
              selectedCodes={selectedCodes}
            />
          )}
        </Stack>
      </Container>
      <CCIAlertBottomBar open={incorrectCodeCombinations} />
    </Box>
  );
};

export default EditSheetScreen;
